import { calculateRankFromMetrics, DEFAULT_BASELINE_METRICS, calculateRankConfidence } from './rankingFormula';

interface Initiative {
  id: string;
  name: string;
  category: string;
  costRange: {
    min: number;
    max: number;
  };
  metrics: {
    name: string;
    impact: number;
  }[];
  description: string;
}

interface ScenarioImpact {
  projectedRank: number;
  rankChange: number;
  categoryContributions: {
    [key: string]: number;
  };
  confidenceInterval: {
    lower: number;
    upper: number;
  };
}

// Calculate diminishing returns for multiple initiatives in the same category
function calculateDiminishingReturns(impacts: number[]): number {
  if (impacts.length === 0) return 0;
  if (impacts.length === 1) return impacts[0];

  // Sort impacts in descending order
  const sortedImpacts = [...impacts].sort((a, b) => b - a);
  
  // Each subsequent initiative has diminishing effect
  return sortedImpacts.reduce((total, impact, index) => {
    const diminishedImpact = impact * Math.pow(0.8, index); // 20% diminishing returns
    return total + diminishedImpact;
  }, 0);
}

// Calculate initiative weight based on cost
function calculateInitiativeWeight(costRange: { min: number; max: number }): number {
  const avgCost = (costRange.min + costRange.max) / 2;
  // Logarithmic scaling to handle large cost ranges
  return Math.log10(avgCost / 100000) * 0.1;
}

// Map initiative metrics to detailed categories
function mapMetricToCategory(metricName: string): string | null {
  const metricMapping: { [key: string]: { category: string; subMetric: string } } = {
    // Outcomes metrics
    'Graduation Rate': { category: 'outcomes', subMetric: 'graduationRate' },
    'Retention Rate': { category: 'outcomes', subMetric: 'retentionRate' },
    'Pell Grant Graduation Rate': { category: 'outcomes', subMetric: 'pellGrantGradRate' },
    'Non-Pell Grant Graduation Rate': { category: 'outcomes', subMetric: 'nonPellGrantGradRate' },
    'Overperformance': { category: 'outcomes', subMetric: 'overperformance' },
    'Median Debt': { category: 'outcomes', subMetric: 'medianDebt' },
    'Graduate Earnings': { category: 'outcomes', subMetric: 'graduatesEarning' },
    
    // Expert Opinion metrics
    'Peer Assessment': { category: 'expertOpinion', subMetric: 'peerAssessment' },
    
    // Faculty Resources metrics
    'Faculty Salaries': { category: 'facultyResources', subMetric: 'facultySalaries' },
    'Full-Time Faculty (2%)': { category: 'facultyResources', subMetric: 'fullTimeFaculty2' },
    'Full-Time Faculty (3%)': { category: 'facultyResources', subMetric: 'fullTimeFaculty3' },
    'Student-Faculty Ratio': { category: 'facultyResources', subMetric: 'studentFacultyRatio' },
    
    // Financial Resources metrics
    'Financial Resources': { category: 'financialResources', subMetric: 'financialResourcesRank' },
    
    // Student Excellence metrics
    'SAT/ACT Scores': { category: 'studentExcellence', subMetric: 'satAct75thPercentile' },
    
    // Faculty Research metrics
    'Research Output': { category: 'facultyResearch', subMetric: 'bibliometricRank' }
  };

  return metricMapping[metricName]?.category || null;
}

// Calculate impact with detailed metric weights
function calculateDetailedImpact(
  metrics: { name: string; impact: number }[],
  weight: number
): { [key: string]: number } {
  const impacts: { [key: string]: number[] } = {};
  
  metrics.forEach(metric => {
    const mapping = mapMetricToCategory(metric.name);
    if (mapping) {
      if (!impacts[mapping]) {
        impacts[mapping] = [];
      }
      impacts[mapping].push(metric.impact * weight);
    }
  });

  // Apply diminishing returns per category
  const finalImpacts: { [key: string]: number } = {};
  Object.entries(impacts).forEach(([category, categoryImpacts]) => {
    finalImpacts[category] = calculateDiminishingReturns(categoryImpacts);
  });

  return finalImpacts;
}

export function calculateScenarioImpact(
  initiatives: Initiative[],
  currentRank: number
): ScenarioImpact {
  // If no initiatives are selected, return current rank with no changes
  if (initiatives.length === 0) {
    return {
      projectedRank: currentRank,
      rankChange: 0,
      categoryContributions: { ...DEFAULT_BASELINE_METRICS },
      confidenceInterval: {
        lower: currentRank,
        upper: currentRank
      }
    };
  }

  // Start with baseline metrics
  const impactedMetrics = { ...DEFAULT_BASELINE_METRICS };
  const categoryImpacts: { [key: string]: number[] } = {};

  // Group impacts by category
  initiatives.forEach(initiative => {
    const weight = calculateInitiativeWeight(initiative.costRange);

    initiative.metrics.forEach(metric => {
      const category = mapMetricToCategory(metric.name);
      if (category) {
        if (!categoryImpacts[category]) {
          categoryImpacts[category] = [];
        }
        categoryImpacts[category].push(metric.impact * weight);
      }
    });
  });

  // Apply diminishing returns and update metrics
  Object.entries(categoryImpacts).forEach(([category, impacts]) => {
    const totalImpact = calculateDiminishingReturns(impacts);
    impactedMetrics[category as keyof typeof impactedMetrics] *= (1 + totalImpact);
  });

  // Calculate new rank
  const projectedRank = calculateRankFromMetrics(impactedMetrics);
  const confidenceInterval = calculateRankConfidence(projectedRank);

  return {
    projectedRank,
    rankChange: currentRank - projectedRank,
    categoryContributions: impactedMetrics,
    confidenceInterval
  };
} 
